import {
  useAuthenticatedQuery,
  useAuthenticatedMutation,
  type ApiQueryOptions,
  type ApiMutationOptions,
} from '@/hooks/useApi';
import {
  useQueryClient,
  useMutation,
  type UseMutationOptions,
} from '@tanstack/react-query';
import type { AxiosResponse } from 'axios';
import adminApi from '@/api/adminApi';
import type {
  ApiResponse,
  AdminMeResponse,
  NormalizedApiError,
  UserAdminModel,
} from '@/api/types';

type AdminPayload = Record<string, unknown>;
type AdminResponse = ApiResponse<UserAdminModel>;
type AdminListResponse = ApiResponse<UserAdminModel[]>;
type AdminSigninResponse = AxiosResponse<ApiResponse<UserAdminModel>>;
type AdminSigninVariables = { email: string; password: string };

// Query keys for admin operations
export const ADMIN_KEYS = {
  all: ['admins'] as const,
  me: ['admins', 'me'] as const,
  byId: (id: string) => ['admins', id] as const,
};

/**
 * Hook to get current admin profile (me)
 */
export function useAdminMe(options: ApiQueryOptions<AdminMeResponse> = {}) {
  return useAuthenticatedQuery<AdminMeResponse>(
    ADMIN_KEYS.me,
    token => adminApi.getMe(token),
    options,
  );
}

/**
 * Hook to get all admins
 */
export function useAllAdmins(options: ApiQueryOptions<AdminListResponse> = {}) {
  return useAuthenticatedQuery<AdminListResponse>(
    ADMIN_KEYS.all,
    token => adminApi.getAllAdmins(token),
    options,
  );
}

/**
 * Hook to get admin by ID
 */
export function useAdminById(
  id: string,
  options: ApiQueryOptions<AdminResponse> = {},
) {
  return useAuthenticatedQuery<AdminResponse>(
    ADMIN_KEYS.byId(id),
    token => adminApi.getAdminById(id, token),
    {
      enabled: !!id,
      ...options,
    },
  );
}

/**
 * Hook to update current admin profile (me)
 */
export function useUpdateAdminMe(
  options: ApiMutationOptions<
    AdminResponse,
    { profileData: AdminPayload }
  > = {},
) {
  const queryClient = useQueryClient();

  return useAuthenticatedMutation<AdminResponse, { profileData: AdminPayload }>(
    ({ profileData, token }) => adminApi.updateMe(profileData, token),
    {
      onSuccess: () => {
        // Invalidate admin me query to refetch updated profile
        queryClient.invalidateQueries({ queryKey: ADMIN_KEYS.me });
      },
      ...options,
    },
  );
}

/**
 * Hook to create new admin
 */
export function useCreateAdmin(
  options: ApiMutationOptions<AdminResponse, { adminData: AdminPayload }> = {},
) {
  const queryClient = useQueryClient();

  return useAuthenticatedMutation<AdminResponse, { adminData: AdminPayload }>(
    ({ adminData, token }) => adminApi.createAdmin(adminData, token),
    {
      onSuccess: () => {
        // Invalidate admins list to show new admin
        queryClient.invalidateQueries({ queryKey: ADMIN_KEYS.all });
      },
      ...options,
    },
  );
}

/**
 * Hook to update admin
 */
export function useUpdateAdmin(
  options: ApiMutationOptions<
    AdminResponse,
    { id: string; adminData: AdminPayload }
  > = {},
) {
  const queryClient = useQueryClient();

  return useAuthenticatedMutation<
    AdminResponse,
    { id: string; adminData: AdminPayload }
  >(({ id, adminData, token }) => adminApi.updateAdmin(id, adminData, token), {
    onSuccess: (_data, variables) => {
      // Invalidate specific admin and list
      queryClient.invalidateQueries({
        queryKey: ADMIN_KEYS.byId(variables.id),
      });
      queryClient.invalidateQueries({ queryKey: ADMIN_KEYS.all });
    },
    ...options,
  });
}

/**
 * Hook to delete admin
 */
export function useDeleteAdmin(
  options: ApiMutationOptions<ApiResponse<null>, { id: string }> = {},
) {
  const queryClient = useQueryClient();

  return useAuthenticatedMutation<ApiResponse<null>, { id: string }>(
    ({ id, token }) => adminApi.deleteAdmin(id, token),
    {
      onSuccess: (_data, variables) => {
        // Remove from cache and invalidate list
        queryClient.removeQueries({ queryKey: ADMIN_KEYS.byId(variables.id) });
        queryClient.invalidateQueries({ queryKey: ADMIN_KEYS.all });
      },
      ...options,
    },
  );
}

/**
 * Hook to update admin status (active/inactive)
 */
export function useUpdateAdminStatus(
  options: ApiMutationOptions<unknown, { id: string; status: unknown }> = {},
) {
  const queryClient = useQueryClient();

  return useAuthenticatedMutation<unknown, { id: string; status: unknown }>(
    ({ id, status, token }) => adminApi.updateAdminStatus(id, status, token),
    {
      onSuccess: (_data, variables) => {
        // Invalidate specific admin and list
        queryClient.invalidateQueries({
          queryKey: ADMIN_KEYS.byId(variables.id),
        });
        queryClient.invalidateQueries({ queryKey: ADMIN_KEYS.all });
      },
      ...options,
    },
  );
}

/**
 * Hook to update admin role
 */
export function useUpdateAdminRole(
  options: ApiMutationOptions<unknown, { id: string; role: unknown }> = {},
) {
  const queryClient = useQueryClient();

  return useAuthenticatedMutation<unknown, { id: string; role: unknown }>(
    ({ id, role, token }) => adminApi.updateAdminRole(id, role, token),
    {
      onSuccess: (_data, variables) => {
        // Invalidate specific admin and list
        queryClient.invalidateQueries({
          queryKey: ADMIN_KEYS.byId(variables.id),
        });
        queryClient.invalidateQueries({ queryKey: ADMIN_KEYS.all });
      },
      ...options,
    },
  );
}

/**
 * Hook for admin signin/login
 * Note: This doesn't use useAuthenticatedMutation since there's no token yet
 */
export function useAdminSignin(
  options: Partial<
    UseMutationOptions<
      AdminSigninResponse,
      NormalizedApiError,
      AdminSigninVariables
    >
  > = {},
) {
  return useMutation<
    AdminSigninResponse,
    NormalizedApiError,
    AdminSigninVariables
  >({
    mutationFn: ({ email, password }) => adminApi.signin(email, password),
    ...options,
  });
}
